import React, { useState, useContext, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { saveAs } from 'file-saver';
import { Grid, Typography } from '@material-ui/core';

import theme from 'styles/theme';
import Table from 'components/table/Table';
import TableHeader from 'components/table/TableHeader';
import TableHeaderAction from 'components/table/TableHeaderAction';
import ReportFilters from 'components/filter/ReportFilters';

import { usePaginatedData } from 'hooks/common';
import { useAccessLog } from 'hooks/logs';
import { useQuery } from 'hooks/fetch';
import { fetchAuthenticated } from 'services/fetch';
import { emitEvent } from 'utils/events';

import { REPORT_COLUMNS } from './columns';
import ReportFilterContext from 'pages/Reports/ReportFilterContext';
import { getDateFiltersFromContext } from 'pages/Reports/helpers';
import { logAction } from 'utils/logs';

const useStyles = makeStyles(theme => ({
  root: { ...theme.custom.page.root },
}));

const PATH = 'equipment-observation/observation-report';
function ObservationsReport(props) {
  useAccessLog('Acesso ao relatório de observações');
  const classes = useStyles();
  const filterContext = useContext(ReportFilterContext);
  const [data, setData] = usePaginatedData();

  const [dataInitalDateFilled, setDataInitalDateFilled] = useState(false);
  const [dataFinalDateFilled, setDataFinalDateFilled] = useState(false);

  const defaultFilter = {
    'equipment.imei': filterContext.equipment ? filterContext.equipment.imei : undefined,
    $expr: { $and: getDateFiltersFromContext(filterContext, '$timestamp') },
  }
  if (filterContext.company) {
    defaultFilter['equipment.currentCompany._id']= {
      value: filterContext.company._id,
      toObjectId: false,
    }
  }

  const [query, setQuery, isLoading] = useQuery(
    PATH,
    setData,
    undefined,
    {
      filter: defaultFilter,
      sort: {
        'date': -1,
      }
    },
    (q) => {
      if(!dataInitalDateFilled|| !dataFinalDateFilled) {
        return false;
      }
      const obj = q.queryObject;
      return Boolean(obj && obj.filter && obj.filter['equipment.imei']);
    },
  );

  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const exportSpreadsheet = async (query) => {
    setIsGeneratingReport(true);
    if (data && data.totalItems && data.totalItems > 5000) {
      emitEvent('showSnack', { message: 'Exportação limitada a 5000 itens', type: 'info' });
    }
    const response = await fetchAuthenticated('GET', `equipment-observation/report/xls${query.queryString}`);
    logAction('Exportou relatório de observações em XLS')
    const blob = await response.blob();
    saveAs(blob, 'Relatorio_Observacoes.xls');
    setIsGeneratingReport(false);
  };

  const exportPdf = async (query) => {
    setIsGeneratingReport(true);
    if (data && data.totalItems && data.totalItems > 5000) {
      emitEvent('showSnack', { message: 'Exportação limitada a 5000 itens', type: 'info' });
    }
    const response = await fetchAuthenticated('GET', `equipment-observation/report/pdf${query.queryString}`);
    logAction('Exportou relatório de observações em PDF')
    const blob = await response.blob();
    saveAs(blob, 'Relatorio_Observacoes.pdf');
    setIsGeneratingReport(false);
  };

  const onCompanyChange = async ({ opt, filterHandler }) => {
    filterContext.updateCompany(opt);
    if (!opt) {
      filterHandler.removeFilter('equipment.currentCompany._id');
    } else {
      filterHandler.updateQueryFilter({
        'equipment.currentCompany._id': {
          value: opt._id,
          toObjectId: false,
        }
      });
    }
  };
  const onEquipmentChange = async ({ opt, filterHandler }) => {
    filterContext.updateEquipment(opt)
    if (!opt) {
      filterHandler.removeFilter('equipment.imei');
    } else {
      filterHandler.updateQueryFilter({
        'equipment.imei': opt.imei,
      });
    }
  };

  const onDateChange = async ({ date, filterHandler }, comparator) => {
    let _andClause = [];

    if (query && query.queryObject) {
      const _obj = { ...query.queryObject.filter };

      // Check if filter already exists and removes it
      if (_obj && _obj.$expr && _obj.$expr.$and) {
        _andClause = _obj.$expr.$and.filter(exp => !exp.hasOwnProperty(`${comparator}`));
      }
    }

    if (date) {
      _andClause.push({
        [`${comparator}`]: ['$date', { $dateFromString: { dateString: date } }],
      });
    }

    // It removes add an empty _andClause if no value is supplied and the
    // correct value otherwise
    filterHandler.updateQueryFilter({
      $expr: { $and: _andClause },
    });
  };

  // const _companyFilterProps =
  const _tableHeaderActions = [
    <TableHeaderAction
      title="Exportar XLS"
      IconProps={{ style: theme.custom.icon }}
      Icon={props => <Typography {...props}>XLS</Typography>}
      onClick={() => exportSpreadsheet(query)}
    />,
    <TableHeaderAction
      title="Exportar PDF"
      IconProps={{ style: theme.custom.icon }}
      Icon={props => <Typography {...props}>PDF</Typography>}
      onClick={() => exportPdf(query)}
    />,
  ];

  return (
    <Grid container direction="column" classes={{ root: classes.root }}>
      <Grid container item>
        <ReportFilters
          path={PATH}
          query={query}
          setQuery={setQuery}
          values={filterContext}
          onCompanyChange={onCompanyChange}
          onEquipmentChange={onEquipmentChange}
          onInitialDateChange={({ date, filterHandler }) => {
            setDataInitalDateFilled(date ? true : false);
            filterContext.updateInitialDate(date);
            onDateChange({ date, filterHandler }, '$gte')
          }}
          onFinalDateChange={({ date, filterHandler }) => {
            setDataFinalDateFilled(date ? true : false);
            filterContext.updateFinalDate(date);
            onDateChange({ date, filterHandler }, '$lte')
          }}
        />
      </Grid>
      <Table
        columns={REPORT_COLUMNS}
        data={data}
        query={query}
        setQuery={setQuery}
        HeaderComponent={<TableHeader headerActions={_tableHeaderActions} />}
        containerStyle={{
          marginTop: 0,
        }}
        isLoading={isLoading || isGeneratingReport}
      />
    </Grid>
  );
}

ObservationsReport.defaultProps = {};

export default ObservationsReport;
